<template>
    <!-- `4 · 8.0 GB · 40.0 GB` with a picture in front of every figure. The plain sentence
         is the accessible name, so a screen reader still hears "4 vCPU, 8 GB, 40 GB". -->
    <span
        :aria-label="plain"
        class="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-xs text-dimmed"
        :title="plain"
    >
        <span class="flex items-center gap-1">
            <UIcon class="size-3.5 shrink-0" name="i-lucide-cpu" />
            {{ cpu }}
        </span>
        <span class="flex items-center gap-1">
            <UIcon class="size-3.5 shrink-0" name="i-lucide-memory-stick" />
            {{ formatBytes(memoryBytes) }}
        </span>
        <span class="flex items-center gap-1">
            <UIcon class="size-3.5 shrink-0" name="i-lucide-hard-drive" />
            {{ formatBytes(wsSizeBytes) }}
        </span>
        <!-- The one figure that is not a number: either the size runs virtual machines or
             the chip is simply not there. -->
        <span v-if="kvm" class="flex items-center gap-1">
            <UIcon class="size-3.5 shrink-0" name="i-lucide-server" />
            {{ $t('instance_type.kvm') }}
        </span>
    </span>
</template>

<script lang="ts" setup>
    import { computed } from 'vue'
    import { useI18n } from 'vue-i18n'

    import { formatBytes } from '@/libs/helpers'

    // Three numbers and nothing else: cores, memory, workspace disk. Which one is which was
    // impossible to tell when they were three figures in a row, so each one carries its own
    // icon — a chip, a memory stick, a disk — and no word.
    const props = defineProps<{
        cpu:         number
        memoryBytes: number
        wsSizeBytes: number
        // Hardware virtualisation is the fourth thing a size can give you, and it is the
        // only one that is a yes or a no — so it is a word rather than a figure.
        kvm?:        boolean
    }>()

    const { t } = useI18n()

    const plain = computed(() => {
        const summary = t('instance_type.summary', {
            cpu:    props.cpu,
            memory: formatBytes(props.memoryBytes),
            ws:     formatBytes(props.wsSizeBytes),
        })

        return props.kvm ? `${summary}, ${t('instance_type.kvm')}` : summary
    })
</script>
